// src/components/ui/layout/UniversalHeader/UniversalHeader.jsx
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Search,
  Plus,
  Menu,
  Bell,
  User,
  Calendar,
  Users,
  FileText,
  Building2,
  LogOut,
  Settings,
  Mail,
  Phone,
  ArrowLeft,
  Check,
  Loader2
} from 'lucide-react';

// shadcn components
import { Button } from '../../button';
import { Input } from '../../input';
import { Separator } from '../../separator';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../form';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../../avatar';

// Sidebar integration
import { useSidebar } from '../../sidebar';

// Auth context
import { useAuth } from '../../../../contexts/AuthContext';

// Customer hook for creation
import { useCustomers } from '../../../../hooks/useCustomers';
import { useToast } from '../../../../hooks/use-toast';

// Customer form validation schema
const customerSchema = z.object({
  name: z
    .string()
    .min(1, 'Customer name is required')
    .max(100, 'Name cannot exceed 100 characters')
    .transform(val => val.trim()),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .transform(val => val.trim().toLowerCase()),
  phone: z
    .string()
    .min(1, 'Phone number is required')
    .refine(val => {
      const digitsOnly = val.replace(/\D/g, '');
      return digitsOnly.length >= 10 && digitsOnly.length <= 15;
    }, 'Phone number must be between 10-15 digits')
    .transform(val => val.trim())
});

const UniversalHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleSidebar } = useSidebar();
  const { currentUser, logout } = useAuth();
  const { createCustomer, error: customerError } = useCustomers();
  const { toast } = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownMode, setDropdownMode] = useState('menu'); // 'menu' or 'customerForm'
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Customer form setup
  const customerForm = useForm({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: ''
    }
  });

  // Generate breadcrumbs from current route
  const generateBreadcrumbs = () => {
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);

    // Base breadcrumb for root
    const breadcrumbs = [
      { label: 'Dashboard', href: '/', isCurrent: path === '/' }
    ];

    // Map route segments to readable names
    const routeMap = {
      'customers': 'Customers',
      'invoices': 'Invoices',
      'appointments': 'Appointments',
      'calendar': 'Calendar',
      'partners': 'Partners',
      'widgets': 'Website Integration',
      'configuration': 'Configuration',
      'business': 'Business Settings',
      'communications': 'Communications',
      'services': 'Services',
      'team': 'Team',
      'create': 'Create',
      'edit': 'Edit'
    };

    // Build breadcrumb path
    let currentPath = '';
    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === segments.length - 1;
      const label = routeMap[segment] || segment;

      breadcrumbs.push({
        label,
        href: currentPath,
        isCurrent: isLast
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Global action handlers
  const handleNewAppointment = () => navigate('/calendar');
  const handleCreateInvoice = () => navigate('/invoices/create');
  const handleAddCustomer = () => {
    setDropdownMode('customerForm');
  };

  // Customer form handlers
  const handleCustomerSubmit = async (customerData) => {
    try {
      setIsSubmitting(true);

      console.log('🆕 Creating customer from header:', customerData);

      createCustomer(customerData);

      toast({
        title: "Success!",
        description: `Customer "${customerData.name}" created successfully!`,
      });

      // Reset form and return to menu
      customerForm.reset();
      setDropdownMode('menu');

    } catch (err) {
      console.error('❌ Customer creation error:', err);
      toast({
        title: "Error",
        description: err.message || 'Failed to create customer. Please try again.',
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToMenu = () => {
    customerForm.reset();
    setDropdownMode('menu');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // TODO: Implement global search
      console.log('Searching for:', searchQuery);
    }
  };

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    const initials = names.map(n => n.charAt(0).toUpperCase()).join('');
    return initials.slice(0, 2);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-14 items-center gap-4 px-4">

        {/* Mobile sidebar toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 md:hidden"
          onClick={toggleSidebar}
        >
          <Menu className="h-4 w-4" />
          <span className="sr-only">Toggle sidebar</span>
        </Button>

        {/* Separator */}
        <Separator orientation="vertical" className="h-4 hidden md:block" />

        {/* Breadcrumbs */}
        <Breadcrumb className="hidden sm:flex">
          <BreadcrumbList>
            {breadcrumbs.map((breadcrumb, index) => (
              <React.Fragment key={breadcrumb.href}>
                <BreadcrumbItem>
                  {breadcrumb.isCurrent ? (
                    <BreadcrumbPage>{breadcrumb.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink
                      href={breadcrumb.href}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(breadcrumb.href);
                      }}
                    >
                      {breadcrumb.label}
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Global Search */}
        <form onSubmit={handleSearch} className="relative hidden md:flex">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search customers, invoices..."
            className="w-64 pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </form>

        {/* Global Actions - Add Button */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-8 w-8">
              <Plus className="h-4 w-4" />
              <span className="sr-only">Add new</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className={dropdownMode === 'customerForm' ? "w-80" : "w-56"}>
            {dropdownMode === 'menu' ? (
              <>
                <DropdownMenuLabel>Create New</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={handleNewAppointment}>
                    <Calendar className="mr-2 h-4 w-4" />
                    <span>New Appointment</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleCreateInvoice}>
                    <FileText className="mr-2 h-4 w-4" />
                    <span>Create Invoice</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleAddCustomer}
                    onSelect={(e) => e.preventDefault()}
                  >
                    <Users className="mr-2 h-4 w-4" />
                    <span>Add Customer</span>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </>
            ) : (
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-sm">Add Customer</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackToMenu}
                    className="h-6 w-6 p-0"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                </div>

                <Form {...customerForm}>
                  <form onSubmit={customerForm.handleSubmit(handleCustomerSubmit)} className="space-y-3">
                    {/* Customer Name */}
                    <FormField
                      control={customerForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Customer Name</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <User className="absolute left-2 top-2.5 h-3 w-3 text-muted-foreground" />
                              <Input
                                {...field}
                                placeholder="Enter name"
                                disabled={isSubmitting}
                                className="pl-7 h-8 text-sm"
                                maxLength={100}
                              />
                            </div>
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    {/* Email */}
                    <FormField
                      control={customerForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Email</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Mail className="absolute left-2 top-2.5 h-3 w-3 text-muted-foreground" />
                              <Input
                                {...field}
                                type="email"
                                placeholder="customer@email.com"
                                disabled={isSubmitting}
                                className="pl-7 h-8 text-sm"
                              />
                            </div>
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    {/* Phone */}
                    <FormField
                      control={customerForm.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Phone</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Phone className="absolute left-2 top-2.5 h-3 w-3 text-muted-foreground" />
                              <Input
                                {...field}
                                type="tel"
                                placeholder="(555) 123-4567"
                                disabled={isSubmitting}
                                className="pl-7 h-8 text-sm"
                              />
                            </div>
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    {/* Buttons */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleBackToMenu}
                        disabled={isSubmitting}
                        className="flex-1 h-8 text-xs"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting || !customerForm.formState.isValid}
                        className="flex-1 h-8 text-xs"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <Check className="mr-1 h-3 w-3" />
                            Create
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </Form>
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Notifications (placeholder for future) */}
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Bell className="h-4 w-4" />
          <span className="sr-only">Notifications</span>
        </Button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-8 w-8 rounded-full">
              <Avatar className="h-8 w-8">
                <AvatarImage src={currentUser?.avatar} alt={currentUser?.name} />
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold">
                  {getUserInitials(currentUser?.name)}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">
                  {currentUser?.name || 'User'}
                </p>
                <p className="text-xs leading-none text-muted-foreground">
                  {currentUser?.email}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => navigate('/configuration/business')}>
                <Building2 className="mr-2 h-4 w-4" />
                <span>Business Settings</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/configuration/team')}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Account Settings</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

      </div>
    </header>
  );
};

export default UniversalHeader;